export interface AIReplySuggestionPreset {
  id: string;
  label: string;
  description: string;
  prompt: string;
}

export const DEFAULT_REPLY_SUGGESTION_PRESETS: AIReplySuggestionPreset[] = [
  {
    id: 'resgatar_conversa',
    label: 'Resgatar conversa',
    description: 'Lead parou de responder há muito tempo',
    prompt: `FOCO: resgatar conversas imobiliárias ANTIGAS, paradas há semanas ou meses. A abordagem precisa soar como uma pessoa real revisitando o atendimento, jamais como automação, IA ou mensagem padrão de vendas.

Use obrigatoriamente a saudação do horário atual informada como "SAUDAÇÃO ATUAL EM SÃO PAULO" ("Bom dia", "Boa tarde" ou "Boa noite"). Comece a sugestão com a saudação seguida do nome do lead quando ele estiver disponível. Exemplo: "Bom dia, João!".

Use o bloco "CONTEXTO DO LEAD" (resumo da qualificação + campos personalizados) como matéria-prima principal. Esses campos costumam trazer região, faixa de valor, tipo de imóvel, número de quartos, finalidade e etapa de negociação. Aproveite o que estiver preenchido. Se não houver nada, use o histórico. Nunca invente região, valor, imóvel ou preferência que não esteja no contexto nem no histórico.

Analise os timestamps antes de escrever:
- Identifique há quanto tempo foi a última interação relevante.
- Se a conversa está parada há semanas ou meses, reconheça de leve, como alguém revisando o atendimento ("estava revisando aqui nossa conversa", "passei pela nossa conversa de um tempo atrás", "fiquei na dúvida aqui se ainda faz sentido continuar").
- Se foi o corretor que parou de responder, retome de forma discreta, sem desculpas longas nem texto engessado.

Pergunta central do resgate (escolha um ângulo por sugestão):
- Se ainda está procurando.
- Se já encontrou (e fechou com outro corretor).
- Se mudou de ideia em algum critério: região, tipo de imóvel, faixa de valor, número de quartos, finalidade (moradia/investimento), apartamento vs casa.
- Se quer retomar agora com opções atualizadas dentro dos critérios que ele já passou.

Exemplos de tom (NÃO copiar literal, são só referência de naturalidade):
- "Bom dia, João, tudo bem? Fiquei na dúvida aqui... você ainda está procurando o apartamento em [região]?"
- "Boa tarde, Maria! Estava revisando aqui nossa conversa e vi que você tava olhando casa em [região] até [faixa]. Ainda faz sentido eu te mandar opções atualizadas?"
- "Boa noite, Pedro! Passei pela nossa conversa e lembrei do seu caso. Mudou alguma coisa na região ou no tipo de imóvel desde a última vez?"

Tom de WhatsApp:
- Natural, humano, curto e consultivo. Máximo 2 frases por sugestão.
- Sem pressão, sem texto longo, sem floreios, sem emojis excessivos.
- NUNCA use travessão. NUNCA use "—" nem " - " para separar ideias. Use vírgula, ponto ou dois-pontos.
- Evite frases com cara de IA ou automação: "Espero que esteja bem", "Estou entrando em contato para", "Gostaria de saber se ainda tem interesse", "Fico à disposição", "Será um prazer ajudar", "Permita-me" e parecidos.
- Evite clichês genéricos que serviriam para qualquer lead. Use detalhe real do CONTEXTO DO LEAD ou do histórico sempre que houver.
- Escreva como um corretor experiente mandaria no WhatsApp: direto, leve, cordial, com uma pergunta simples no final.

Gere 3 opções com ângulos diferentes:
1. Retomada leve reconhecendo o tempo parado, sem dramatizar ("tudo bem? Fiquei na dúvida aqui se ainda...").
2. Conectada a um detalhe específico do CONTEXTO DO LEAD ou do histórico (cite região, tipo, faixa ou etapa real).
3. Perguntando se algo mudou nos critérios (região / tipo / faixa / quartos) OU, se faltar dado, fazendo a pergunta qualificadora que falta.`,
  },
  {
    id: 'resposta_direta',
    label: 'Resposta direta',
    description: 'Responder o que o lead perguntou',
    prompt: 'FOCO: Responder diretamente a última dúvida ou pergunta do lead. Gere mensagens objetivas, naturais e úteis, sem abrir novos assuntos desnecessários.',
  },
];

const DEFAULT_PRESET_BY_ID = new Map(
  DEFAULT_REPLY_SUGGESTION_PRESETS.map((preset) => [preset.id, preset]),
);

const OBSOLETE_DEFAULT_PRESET_IDS = new Set([
  'quebrar_gelo',
  'quebrar_objecao',
  'follow_up',
  'agendar_visita',
  'apresentar_opcoes',
  'fechar_negocio',
]);

export function normalizeReplySuggestionPresets(value: unknown): AIReplySuggestionPreset[] {
  if (!Array.isArray(value)) return DEFAULT_REPLY_SUGGESTION_PRESETS;

  const hasLegacyDefaults = value.some((item) => {
    if (!item || typeof item !== 'object') return false;
    const id = (item as Record<string, unknown>).id;
    return typeof id === 'string' && OBSOLETE_DEFAULT_PRESET_IDS.has(id);
  });

  const presets = value
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const raw = item as Record<string, unknown>;
      const id = typeof raw.id === 'string' ? raw.id.trim() : '';
      if (OBSOLETE_DEFAULT_PRESET_IDS.has(id)) return null;
      if (hasLegacyDefaults && DEFAULT_PRESET_BY_ID.has(id)) {
        return DEFAULT_PRESET_BY_ID.get(id) ?? null;
      }
      const label = typeof raw.label === 'string' ? raw.label.trim() : '';
      const description = typeof raw.description === 'string' ? raw.description.trim() : '';
      const prompt = typeof raw.prompt === 'string' ? raw.prompt.trim() : '';
      if (!id || !label || !prompt) return null;
      return { id, label, description, prompt };
    })
    .filter((item): item is AIReplySuggestionPreset => Boolean(item));

  return presets.length > 0 ? presets : DEFAULT_REPLY_SUGGESTION_PRESETS;
}
